import { DatePipe } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { catchError } from 'rxjs/operators';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { AccidentRecord } from 'src/app/shared/models/accident-record.model';
import { DriverApiService } from 'src/app/shared/services/driver-api.service';
import { environment } from 'src/environments/environment.dev';
import { SubSink } from 'subsink';
import { ACCIDENT_NATURE } from '../shared/data-const/accidentNature';

import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { ROUTE_DQF, ROUTE_DQF_UPLOADS } from 'src/app/shared/routes';
import { MatRadioChange } from '@angular/material/radio';

const REQUIRED_DATE_MESSAGE = 'Select Date';
const REQUIRED_NATURE_MESSAGE = 'Select Nature of Accident';
const REQUIRED_FATALITIES_MESSAGE = 'Select Number of Fatalities';
const REQUIRED_INJURIES_MESSAGE = 'Select Number of Injuries';
const REQUIRED_CHEMICAL_SPILLS_MESSAGE = 'Select Chemical Spills';
const ACTION_SAVE_ACCIDENT_RECORD = 'Save accident record';
const ACTION_UPDATE_ACCIDENT_RECORD = 'Update accident record';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/dd/yyyy'
  },
  display: {
    dateInput: 'dd MMM yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy'
  }
};

@Component({
  selector: 'app-accident-records',
  templateUrl: './accident-records.component.html',
  styleUrls: ['./accident-records.component.scss'],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    DatePipe
  ],
  standalone: false
})
export class AccidentRecordsComponent implements OnInit, OnDestroy {
  openAccidentForm = false;
  allowEdit = false;
  isNoAccidentPresent: boolean = false;
  radioSaving: boolean = false;
  errorTimeout = null;
  errorMessage: string;
  accidentRecords: AccidentRecord[];
  noAccidentRecord: AccidentRecord;
  noAccidentRecordsForm: UntypedFormGroup;
  accidentForm: UntypedFormGroup;
  disableNoAccidents: boolean;
  maxDate: Date = new Date();
  enableSave: boolean = false;
  hasExplicitSelection = false;
  accidentNatures = ACCIDENT_NATURE;
  isContentReady: boolean = false;
  numInvolved = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10+'];

  private subs = new SubSink();

  constructor(
    private readonly registrationService: RegistrationService,
    private readonly location: Location,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly datePipe: DatePipe,
    private readonly driverApiService: DriverApiService,
    private readonly dateTimeService: DateTimeService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute
  ) {
    this.errorMessage = undefined;
    this.accidentForm = this.createForm();
    this.noAccidentRecordsForm = this.createNoAccidentRecordsForm();
  }

  ngOnInit(): void {
    this.loadAccidentRecords();
    this.subs.sink = this.noAccidentRecordsForm.get('noAccidents').valueChanges.subscribe(value => {
      if (value != this.noAccidentRecord?.noAccidents) {
        this.enableSave = true;
      } else {
        this.enableSave = false;
      }
      if (!this.noAccidentRecord?.noAccidents) {
        if (value) {
          this.allowEdit = true;
        } else {
          this.allowEdit = false;
        }
      }
    });
  }

  loadAccidentRecords(): void {
    this.accidentRecords = [];
    this.isContentReady = false;
    this.subs.sink = this.registrationService.getAccidentRecords().subscribe(records => {
      const sortedRecords = records.sort(
        (a, b) => {
          const dateA = a.accidentDate ? new Date(a.accidentDate).getTime() : 0;
          const dateB = b.accidentDate ? new Date(b.accidentDate).getTime() : 0;
          return dateB - dateA;
        }
      );
      sortedRecords.forEach(record => {
        if (record.hasOwnProperty('noAccidents')) {
          this.noAccidentRecord = record;
        } else {
          this.accidentRecords.push(record);
        }
        // Parse date string to Date object - handles both YYYY-MM-DD and YYYYMMDD formats
        if (record.accidentDate) {
          const dateStr = record.accidentDate.toString();
          
          // Check if it's YYYY-MM-DD format (with dashes)
          if (dateStr.includes('-')) {
            // Use Date constructor which handles YYYY-MM-DD format correctly
            // Create date at noon UTC to avoid timezone issues
            const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
            record.accidentDate = new Date(year, month - 1, day);
          } else {
            // Handle YYYYMMDD format (no dashes)
            const year = parseInt(dateStr.substring(0, 4), 10);
            const month = parseInt(dateStr.substring(4, 6), 10) - 1; // Month is 0-indexed
            const day = parseInt(dateStr.substring(6, 8), 10);
            record.accidentDate = new Date(year, month, day);
          }
        }
      });
      if (this.noAccidentRecord) {
        this.allowEdit = this.noAccidentRecord?.noAccidents && this.noAccidentRecord.noAccidents ? true : false;
        this.populateNoAccidentForm(this.noAccidentRecord);
      }
      if (this.accidentRecords && this.accidentRecords.length > 0) {
        this.disableNoAccidents = true;
      } else {
        this.disableNoAccidents = false;
      }
      this.isContentReady = true;
    });
  }

  onAddNewAccidentRecord(): void {
    this.openAccidentForm = true;
    this.allowEdit = true;
    this.disableNoAccidents = true;
  }

  onCancel(): void {
    this.openAccidentForm = false;
    this.allowEdit = false;
    this.disableNoAccidents = false;
    this.accidentForm.reset();
  }

  goBack(): void {
    const isFromDQF = this.isNavigatedFromDQF();
    if (isFromDQF) {
      // Navigate back to DQF uploads page
      this.router.navigateByUrl(`${ROUTE_DQF}/${ROUTE_DQF_UPLOADS}?type=Driver Uploads`);
    } else {
      this.location.back();
    }
  }

  private isNavigatedFromDQF(): boolean {
    // Check if we came from DQF by looking at the referrer or route history
    const referrer = document.referrer;

    // Check if referrer contains DQF routes
    if (referrer && (referrer.includes('/driverQualification/') || referrer.includes('/dqf/'))) {
      return true;
    }

    // Check if there's a DQF context in session storage or navigation state
    const navigationState = this.router.getCurrentNavigation()?.extras?.state;
    if (navigationState && navigationState['fromDQF']) {
      return true;
    }

    // Check if we have DQF-related query params or state
    if (this.activatedRoute.snapshot.queryParams['fromDQF'] ||
      this.activatedRoute.snapshot.queryParams['dqfId']) {
      return true;
    }

    return false;
  }

  onSubmitAccidentRecord(formType: String): void {
    if (!this.accidentForm.valid && !this.noAccidentRecordsForm.valid) {
      return;
    }

    let model;
    if (formType == 'addNewRecord') {
      model = !model && this.accidentForm.dirty ? this.prepareAccidentSaveModel() : model;
    } else if (formType == 'noAccident') {
      model = this.noAccidentRecordsForm.dirty ? this.prepareNoAccidentRecordsSaveModel() : undefined;
    }
    if (model && model.id) {
      let id = model.id;
      delete model.id;
      this.onAccidentRecordUpdated(model, id);
    } else if (model) {
      if (model.id == '' || model.id == null) {
        delete model.id;
      }
      this.onAccidentRecordAdded(model);
    } else {
      console.log(`Error: There is no data to save.`);
    }
  }
  getAccidentRadioValue(): string | null {
    const noAccidents = this.noAccidentRecordsForm.get('noAccidents').value;
    if (noAccidents === true) return 'no';
    if (this.accidentRecords?.length > 0) return 'yes';
    if (this.hasExplicitSelection && noAccidents === false) return 'yes';
    if (this.noAccidentRecord && noAccidents === false) return 'yes';
    return null;
  }

  onAccidentRadioChange(event: MatRadioChange): void {
    this.hasExplicitSelection = true;
    if (event.value === 'no') {
      this.noAccidentRecordsForm.get('noAccidents').setValue(true);
      this.noAccidentRecordsForm.markAsDirty();
      this.allowEdit = true;
      this.changeNoAccident();
    } else {
      this.noAccidentRecordsForm.get('noAccidents').setValue(false);
      this.noAccidentRecordsForm.markAsDirty();
      this.allowEdit = false;
      this.changeNoAccident();
    }
  }

  changeNoAccident() {
    this.radioSaving = true;
    const model = this.prepareNoAccidentRecordsSaveModel();
    if (model && model.id) {
      let id = model.id;
      delete model.id;
      this.onAccidentRecordUpdated(model, id, true);
    } else if (model) {
      if (model.id == '' || model.id == null) {
        delete model.id;
      }
      this.onAccidentRecordAdded(model, true);
    } else {
      this.radioSaving = false;
      console.log(`Error: There is no data to save.`);
    }
  }
  onUpdateAccidentPressed($event): void {
    this.allowEdit = true;
    this.openAccidentForm = true;
    this.disableNoAccidents = true;

    if ($event) {
      this.populateForm($event);
    }
  }

  onDeleteAccidentPressed($event: AccidentRecord): void {
    this.isContentReady = false;
    this.subs.add(
      this.registrationService.deleteAccidentRecord($event.id).subscribe(
        () => {
          this.errorMessage = undefined;
          this.allowEdit = false;
          this.loadAccidentRecords();
        },
        () => {
          this.isContentReady = true;
          this.setErrorMessage(environment.errorMessage);
        }
      )
    );
  }

  get accidentDate() {
    return this.accidentForm.get('accidentDate');
  }

  get natureOfAccident() {
    return this.accidentForm.get('natureOfAccident');
  }

  get numOfFatalities() {
    return this.accidentForm.get('numOfFatalities');
  }

  get numOfInjuries() {
    return this.accidentForm.get('numOfInjuries');
  }

  get chemicalsSpilt() {
    return this.accidentForm.get('chemicalsSpilt');
  }

  getAccidentDateErrorMessage(): string {
    return this.accidentDate.errors?.required ? REQUIRED_DATE_MESSAGE : '';
  }

  getNatureOfAccidentErrorMessage(): string {
    return this.natureOfAccident.errors?.required ? REQUIRED_NATURE_MESSAGE : '';
  }

  getNumOfFatalitiesErrorMessage(): string {
    return this.numOfFatalities.errors?.required ? REQUIRED_FATALITIES_MESSAGE : '';
  }

  getNumOfInjuriesErrorMessage(): string {
    return this.numOfInjuries.errors?.required ? REQUIRED_INJURIES_MESSAGE : '';
  }

  getChemicalsSpiltErrorMessage(): string {
    return this.chemicalsSpilt.errors?.required ? REQUIRED_CHEMICAL_SPILLS_MESSAGE : '';
  }

  private onAccidentRecordAdded(model: AccidentRecord, fromRadio = false): void {
    if (!fromRadio) {
      this.isContentReady = false;
    }
    this.subs.add(
      this.registrationService
        .saveAccidentRecord(model)
        .pipe(
          catchError((error: any) => {
            this.setErrorMessage(error.message);
            this.radioSaving = false;
            this.isContentReady = true;
            return of(false);
          })
        )
        .subscribe(() => {
          this.radioSaving = false;
          this.setErrorMessage(this.errorMessage ? this.errorMessage : undefined);
          this.accidentForm.reset();
          this.resetFlags();

          if (model.noAccidents) {
            this.goBack();
          }
          if (!this.errorMessage) {
            this.loadAccidentRecords();
          }
        })
    );
  }

  private onAccidentRecordUpdated(model: AccidentRecord, id: string, fromRadio = false): void {
    if (!fromRadio) {
      this.isContentReady = false;
    }
    this.subs.add(
      this.registrationService
        .updateAccidentRecord(model, id)
        .pipe(
          catchError((error: any) => {
            this.setErrorMessage(error.message);
            this.radioSaving = false;
            this.isContentReady = false;
            return of(false);
          })
        )
        .subscribe(() => {
          this.radioSaving = false;
          this.setErrorMessage(this.errorMessage ? this.errorMessage : undefined);
          this.accidentForm.reset();
          this.resetFlags();
          if (model.noAccidents) {
            this.goBack();
          }

          if (!this.errorMessage) {
            this.loadAccidentRecords();
          }
        })
    );
  }

  private resetFlags(): void {
    this.allowEdit = false;
    this.openAccidentForm = false;
    this.disableNoAccidents = false;
  }

  private prepareAccidentSaveModel(): AccidentRecord {
    const formModel = this.accidentForm.value;

    const model = {
      id: formModel.id as string,
      accidentDate: this.formatDateWithoutTimezone(formModel.accidentDate),
      natureOfAccident: formModel.natureOfAccident as string,
      numOfFatalities: formModel.numOfFatalities as string,
      numOfInjuries: formModel.numOfInjuries as string,
      chemicalsSpilt: formModel.chemicalsSpilt === 'true',
      notes: formModel.notes?.trim() || ''
    };

    return model as AccidentRecord;
  }

  /**
   * Formats a Date object to 'yyyy-MM-dd' string without timezone conversion.
   * This prevents date shifting issues when the datepicker's date is converted to a string.
   */
  private formatDateWithoutTimezone(date: Date): string {
    if (!date) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }

  private prepareNoAccidentRecordsSaveModel(): AccidentRecord {
    const formModel = this.noAccidentRecordsForm.value;
    const model = {
      id: formModel.id as string,
      noAccidents: formModel.noAccidents as boolean
    } as AccidentRecord;

    return model as AccidentRecord;
  }

  private resetNoAccidentRecordsForm(accidentRecord?: AccidentRecord): void {
    this.noAccidentRecordsForm.reset({
      id: accidentRecord ? accidentRecord.id : '',
      noAccidents: accidentRecord ? accidentRecord.noAccidents : false
    });
  }

  private createNoAccidentRecordsForm(): UntypedFormGroup {
    const form = this.formBuilder.group({
      id: [''],
      noAccidents: [false]
    });
    return form;
  }

  private createForm(): UntypedFormGroup {
    const form = this.formBuilder.group({
      id: [''],
      accidentDate: ['', Validators.required],
      natureOfAccident: ['', Validators.required],
      numOfFatalities: ['', Validators.required],
      numOfInjuries: ['', Validators.required],
      chemicalsSpilt: ['', Validators.required],
      notes: [
        '',
        [Validators.maxLength(140), Validators.pattern(/^[a-zA-Z0-9*&().,;#@!$-\s]*$/)]
      ]
    });

    return form;
  }

  private populateForm(accident: AccidentRecord): void {
    this.accidentForm.setValue({
      id: accident.id,
      accidentDate: accident.accidentDate,
      natureOfAccident: accident.natureOfAccident,
      numOfFatalities: accident.numOfFatalities,
      numOfInjuries: accident.numOfInjuries,
      chemicalsSpilt: accident.chemicalsSpilt ? 'true' : 'false',
      notes: accident.notes || ''
    });
  }
  private populateNoAccidentForm(accident: AccidentRecord) {
    this.noAccidentRecordsForm.setValue({
      id: accident.id,
      noAccidents: accident.noAccidents
    });
  }

  private setErrorMessage(message: string): void {
    this.errorMessage = message;

    if (this.errorMessage) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = setTimeout(() => {
        this.errorMessage = undefined;
      }, 7000);
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
